/* ============================================================
   Client-import parsing engine — pure functions shared by the
   web wizard (ImportClients.tsx) and the mobile importer
   (ClientImportScreen). No React, no store, no xlsx, no Client
   type: each app keeps its own platform readers (xlsx / File /
   csv) and the Client-coupled assembly (buildImportRows,
   detectClientLog) local, since the Client shape differs per app.

   Sources feed in as already-parsed grids (CSV text here; xlsx is
   decoded app-side and handed to gridToSource), plus public Google
   Sheets via the gviz CSV export.
============================================================ */

export interface ParsedSource {
  fileName: string;
  tabName?: string;          // workbook tab this slice came from
  headers: string[];
  rows: string[][];
  preHeader?: string[][];    // banner rows above the detected header — kept so
                             // grid-shaped extractors (importHistory) lose nothing
}

export type TargetField = 'name' | 'email' | 'phone' | 'startDate' | 'goals' | 'injuries' | 'note';
export type Mapping = Partial<Record<TargetField, string>>;

export const TARGET_FIELDS: { field: TargetField; label: string; required?: boolean }[] = [
  { field: 'name', label: 'Name', required: true },
  { field: 'email', label: 'Email' },
  { field: 'phone', label: 'Phone' },
  { field: 'startDate', label: 'Start date' },
  { field: 'goals', label: 'Goals' },
  { field: 'injuries', label: 'Injuries' },
  { field: 'note', label: 'Note' },
];

export const ROW_CAP = 2000;

/* ---------- CSV ---------- */

// Quote-aware CSV parser: handles "a, b", escaped "" quotes, CR/LF/CRLF, BOM.
export function parseCsvText(text: string): { headers: string[]; rows: string[][] } {
  const src = text.replace(/^﻿/, '');
  const out: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; }
        else inQuotes = false;
      } else cell += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell); cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      out.push(row); row = [];
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || row.length > 0) { row.push(cell); out.push(row); }

  const nonEmpty = out.filter((r) => r.some((c) => c.trim() !== ''));
  if (nonEmpty.length === 0) return { headers: [], rows: [] };
  const headers = dedupeHeaders(nonEmpty[0].map((h) => h.trim()));
  return { headers, rows: nonEmpty.slice(1) };
}

// Duplicate header names get a numeric suffix so mapping selects stay unambiguous.
function dedupeHeaders(headers: string[]): string[] {
  const seen: Record<string, number> = {};
  return headers.map((h) => {
    const base = h || 'Column';
    seen[base] = (seen[base] || 0) + 1;
    return seen[base] === 1 ? base : `${base} (${seen[base]})`;
  });
}

/* ---------- files (CSV + Excel) ---------- */

// Sheets often open with banner/title rows before the real header. Score the
// first rows for header-ness: filled, texty, unique values, and a TYPE CONTRAST
// with the rows beneath (headers are text; data below them is often not).
export function detectHeaderRow(grid: string[][]): number {
  const isNumish = (v: string) => /^[\d$%.,:/\s-]+$/.test(v) && /\d/.test(v);
  let best = 0;
  let bestScore = -1;
  const limit = Math.min(grid.length - 1, 9);
  for (let i = 0; i <= limit; i++) {
    const row = grid[i];
    const filled = row.filter((c) => c.trim() !== '');
    if (filled.length < 2) continue;
    const nonEmpty = filled.length / Math.max(1, row.length);
    const texty = filled.filter((c) => !isNumish(c)).length / filled.length;
    const unique = new Set(filled.map((c) => c.toLowerCase())).size / filled.length;
    let contrast = 0;
    const below = grid.slice(i + 1, i + 6);
    if (below.length) {
      let comparisons = 0;
      let differs = 0;
      row.forEach((cell, col) => {
        if (cell.trim() === '') return;
        below.forEach((b) => {
          const v = (b[col] || '').trim();
          if (!v) return;
          comparisons++;
          if (isNumish(v) !== isNumish(cell)) differs++;
        });
      });
      contrast = comparisons ? differs / comparisons : 0;
    }
    const score = nonEmpty + texty + unique * 0.5 + contrast;
    if (score > bestScore) { bestScore = score; best = i; }
  }
  return best;
}

export const gridToSource = (grid: string[][], fileName: string, tabName?: string): ParsedSource => {
  if (grid.length === 0) return { fileName, tabName, headers: [], rows: [] };
  const h = detectHeaderRow(grid);
  return {
    fileName,
    tabName,
    headers: dedupeHeaders(grid[h].map((c) => c.trim())),
    rows: grid.slice(h + 1),
    preHeader: h > 0 ? grid.slice(0, h) : undefined,
  };
};

/* ---------- Google Sheets link ---------- */

// Public-link sheets export CSV via the gviz endpoint (CORS-friendly, unlike /export).
export function sheetLinkToGvizCsv(link: string): { url: string } | { error: string } {
  const m = link.match(/\/spreadsheets\/d\/([\w-]+)/);
  if (!m) return { error: 'That doesn’t look like a Google Sheets link.' };
  const gid = link.match(/[#?&]gid=(\d+)/)?.[1];
  return { url: `https://docs.google.com/spreadsheets/d/${m[1]}/gviz/tq?tqx=out:csv${gid ? `&gid=${gid}` : ''}` };
}

export async function fetchSheetCsv(url: string): Promise<string> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    throw new Error('Couldn’t reach Google Sheets — check the link and your connection.');
  }
  const body = await res.text();
  // Private sheets bounce to an HTML login page — frequently with status 200.
  if (!res.ok || res.headers.get('content-type')?.includes('text/html') || body.trimStart().startsWith('<')) {
    throw new Error('Sheet isn’t shared — set it to “Anyone with the link can view” and try again.');
  }
  return body;
}

/* ---------- mapping engine ----------
   Headers lie ("Contact", "Athlete", or a banner row); data doesn't. Each
   column gets a per-field score = trainer-lexicon header match BLENDED with
   content sniffing over a sample of its values, then fields are assigned
   greedily best-first. Works on headerless sheets via content alone. */

const FIELD_KEYWORDS: [TargetField, RegExp][] = [
  ['name', /^(full\s*)?name$|client|athlete|member|participant|^first|^last/i],
  ['email', /e-?mail/i],
  ['phone', /phone|mobile|cell|contact\s*(#|no|num)?$|tel|whats/i],
  ['startDate', /start|joined?|since|onboard|begin/i],
  ['goals', /goal|objective|target|focus|why|outcome|priorit/i],
  ['injuries', /injur|limitation|medical|condition|health|par-?q|pain/i],
  ['note', /note|comment|misc|other|special|package|plan|insta|handle|emergency|dob|birth|source|referr/i],
];

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RX = /^[+(]?(?:[\d][\s\-().]?){7,15}$/;
const NAME_RX = /^[A-Z][A-Za-z'’.-]*(?:\s+[A-Z][A-Za-z'’.-]*){0,3}$/;

interface ColumnProfile {
  filled: number;       // how many sampled cells had content
  email: number;        // ratios over filled sample
  phone: number;
  date: number;
  personName: number;
  longText: number;
}

function profileColumn(values: string[]): ColumnProfile {
  const sample = values.map((v) => v.trim()).filter(Boolean).slice(0, 50);
  const n = sample.length;
  if (n === 0) return { filled: 0, email: 0, phone: 0, date: 0, personName: 0, longText: 0 };
  const ratio = (fn: (v: string) => boolean) => sample.filter(fn).length / n;
  return {
    filled: n,
    email: ratio((v) => EMAIL_RX.test(v)),
    phone: ratio((v) => PHONE_RX.test(v) && !EMAIL_RX.test(v)),
    date: ratio((v) => normalizeDate(v) !== undefined && !/^[A-Za-z' ]+$/.test(v)),
    personName: ratio((v) => NAME_RX.test(v) && !/\d/.test(v) && v.length <= 40),
    longText: ratio((v) => v.length > 25),
  };
}

// How strongly a column's CONTENT suggests a field (0..1).
function contentScore(field: TargetField, p: ColumnProfile): number {
  if (p.filled === 0) return 0;
  switch (field) {
    case 'email': return p.email;
    case 'phone': return p.phone * (1 - p.email);
    case 'startDate': return p.date;
    case 'name': return p.personName * (1 - p.email) * (1 - p.date) * (1 - p.phone);
    case 'goals': return p.longText * 0.5;
    case 'injuries': return p.longText * 0.3;
    case 'note': return p.longText * 0.2;
  }
}

export interface SourceAnalysis {
  mapping: Mapping;
  /* roster-likeness used to rank tabs (name+email weigh heaviest) */
  score: number;
}

export function analyzeSource(src: ParsedSource): SourceAnalysis {
  const profiles = src.headers.map((_, col) => profileColumn(src.rows.map((r) => r[col] || '')));
  const candidates: { field: TargetField; header: string; score: number }[] = [];
  src.headers.forEach((header, col) => {
    for (const [field, rx] of FIELD_KEYWORDS) {
      const headerHit = rx.test(header) ? 1 : 0;
      const score = headerHit + contentScore(field, profiles[col]) * 0.9;
      if (score >= 0.45) candidates.push({ field, header, score });
    }
  });
  candidates.sort((a, b) => b.score - a.score);
  const mapping: Mapping = {};
  const usedHeaders = new Set<string>();
  for (const c of candidates) {
    if (mapping[c.field] || usedHeaders.has(c.header)) continue;
    mapping[c.field] = c.header;
    usedHeaders.add(c.header);
  }
  const weights: Record<TargetField, number> = { name: 3, email: 2, phone: 1, startDate: 0.5, goals: 0.5, injuries: 0.5, note: 0.25 };
  const mappedScore = (Object.keys(mapping) as TargetField[]).reduce((n, f) => n + weights[f], 0);
  return { mapping, score: mappedScore + Math.min(2, Math.log10(src.rows.length + 1)) };
}

// Back-compat wrapper (manual flow + older tests): lexicon-only, no content.
export function guessMapping(headers: string[]): Mapping {
  return analyzeSource({ fileName: '', headers, rows: [] }).mapping;
}

/* ---------- vertical "profile" sheets ----------
   Label:value layouts (col A = labels, col B = values) — like the info tab of
   a per-client training log — transpose into a one-row table so the same
   mapping engine can read them. */
export function transposeVertical(src: ParsedSource): ParsedSource | null {
  const grid = [src.headers, ...src.rows];
  const used = grid.filter((r) => r.some((c) => c.trim() !== ''));
  if (used.length < 3) return null;
  const narrow = used.every((r) => r.filter((c) => c.trim() !== '').length <= 3);
  if (!narrow) return null;
  const labelish = used.filter((r) => {
    const a = (r[0] || '').trim();
    return a !== '' && !/^\d+[\d.,%]*$/.test(a);
  });
  if (labelish.length / used.length < 0.6) return null;
  const headers: string[] = [];
  const row: string[] = [];
  for (const r of used) {
    const label = (r[0] || '').trim().replace(/:$/, '');
    const value = (r.slice(1).find((c) => c.trim() !== '') || '').trim();
    if (!label) continue;
    headers.push(label);
    row.push(value);
  }
  if (headers.length < 2) return null;
  return { fileName: src.fileName, tabName: src.tabName, headers: dedupeHeaders(headers), rows: [row] };
}

/* ---------- clean-start template (offered, never forced) ---------- */
export const TEMPLATE_CSV =
  'Name,Email,Phone,Start Date,Goals,Injuries,Notes\n' +
  'Jane Doe,jane@email.com,(555) 555-0100,2026-01-15,"Lose 10 lb, first pull-up",Left knee — avoid deep lunges,Referred by Sam\n';

/* ---------- dates ---------- */

export const isoLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Returns ISO yyyy-mm-dd, or undefined — a bad date drops the FIELD, never the row.
export function normalizeDate(raw: string): string | undefined {
  const v = raw.trim();
  if (!v) return undefined;
  // Excel serial (days since 1899-12-30) — covers ~1954..2064.
  if (/^\d+(\.\d+)?$/.test(v)) {
    const n = parseFloat(v);
    if (n > 20000 && n < 60000) {
      const d = new Date(Date.UTC(1899, 11, 30) + n * 86400000);
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    }
    return undefined;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const slash = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (slash) {
    let [, a, b, y] = slash;
    let month = parseInt(a, 10), day = parseInt(b, 10);
    if (month > 12 && day <= 12) [month, day] = [day, month]; // DD/MM input
    if (month > 12 || day > 31) return undefined;
    const year = y.length === 2 ? `20${y}` : y;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  const t = Date.parse(v); // "Jan 5, 2024" etc.
  return isNaN(t) ? undefined : isoLocal(new Date(t));
}
